/**
 * city/vehicles/vehicles.js
Dựng xe cộ giao thông đường phố: Xe điện VinFast SUV, Taxi xanh SM, Xe máy số Wave
 */

import * as THREE from 'three';
import { shade } from '../builder-core.js';

class MethodsHolder {
  _addWheel(parent, x, y, z, { r, width, tire, rim, spoke, spokes = 5, outer = 1, bothSides = false }) {
    const pivot = new THREE.Group();
    pivot.position.set(x, y, z);
    parent.add(pivot);
    const spin = new THREE.Group();
    pivot.add(spin);
    this.cyl(spin, r, r, width, 20, tire, 0, 0, 0, [Math.PI / 2, 0, 0]);
    this.cyl(spin, r * 0.68, r * 0.68, width + 0.012, 20, rim, 0, 0, 0, [Math.PI / 2, 0, 0], false);
    const rr = r * 0.66;
    (bothSides ? [-1, 1] : [outer]).forEach((f) => {
      const fz = f * (width / 2 + 0.008);
      for (let i = 0; i < spokes; i++) {
        const a = (i / spokes) * Math.PI * 2;
        this.box(
          spin,
          r * 0.15,
          rr * 0.9,
          0.012,
          spoke,
          Math.cos(a) * rr * 0.45,
          Math.sin(a) * rr * 0.45,
          fz,
          [0, 0, a - Math.PI / 2],
          false
        );
      }
      this.cyl(spin, r * 0.17, r * 0.17, 0.016, 12, spoke, 0, 0, fz, [Math.PI / 2, 0, 0], false);
    });
    return spin;
  }

  // Ô TÔ ĐIỆN VINFAST SUV (bản Taxi): hốc bánh thật, cabin vát, trụ A/B/C/D, dải LED chữ V
  createVinFastCar(isTaxi = false, options = {}) {
    const car = new THREE.Group();
    car.name = isTaxi ? 'VinFast_Taxi_XanhSM' : 'VinFast_SUV';

    const paintColor = options.color || (isTaxi ? '#12b5c9' : '#1e3a8a');
    const paint = this.mat(paintColor, { roughness: 0.28, metalness: 0.35 });
    const paintDeep = this.mat(shade(paintColor, -0.3), { roughness: 0.35, metalness: 0.3 });
    const cladding = this.mat('#1c1917', { roughness: 0.9 });
    const piano = this.mat('#0b0b0c', { roughness: 0.2, metalness: 0.5 });
    const glass = this.mat('#3b5876', { roughness: 0.05, metalness: 0.55 });
    const chrome = this.mat('#e5e7eb', { roughness: 0.15, metalness: 0.95 });
    const tire = this.mat('#161616', { roughness: 0.95 });
    const rimDark = this.mat('#374151', { roughness: 0.35, metalness: 0.7 });
    const ledWhite = this.glow('#f8fafc');
    const ledRed = this.glow('#ef4444');
    const amber = this.glow('#f59e0b');

    const L = 4.3,
      W = 1.86,
      hl = L / 2,
      hw = W / 2;
    const axle = 1.36,
      wr = 0.36,
      gap = 0.44;

    // 1. THÂN XE chia khối để chừa HỐC BÁNH XE
    this.box(car, L, 0.3, W, paint, 0, 0.88, 0);
    const midLen = 2 * (axle - gap);
    this.box(car, midLen, 0.42, W, paint, 0, 0.52, 0);
    const ohLen = hl - (axle + gap);
    [-1, 1].forEach((s) => {
      this.box(car, ohLen, 0.42, W, paint, s * (hl - ohLen / 2), 0.52, 0);
      this.box(car, gap * 2, 0.42, W - 0.5, cladding, s * axle, 0.52, 0, null, false);
      [-1, 1].forEach((sz) => {
        const zf = sz * (hw + 0.012);
        this.box(car, gap * 2 + 0.1, 0.06, 0.03, cladding, s * axle, 0.755, zf);
        [-1, 1].forEach((e) =>
          this.box(car, 0.06, 0.28, 0.03, cladding, s * axle + e * (gap + 0.02), 0.6, zf, [0, 0, -e * 0.3])
        );
      });
    });
    this.box(car, midLen, 0.1, W + 0.02, cladding, 0, 0.36, 0);
    this.box(car, 0.14, 0.15, W + 0.02, cladding, hl - 0.05, 0.36, 0);
    this.box(car, 0.14, 0.17, W + 0.02, cladding, -hl + 0.05, 0.37, 0);
    this.box(car, 1.25, 0.06, W - 0.06, paint, 1.5, 1.05, 0, [0, 0, -0.06]);

    // 2. CABIN KÍNH VÁT (extrude theo mặt cắt hông xe)
    const cabinDepth = W - 0.2;
    const cabinGeo = this._geo('car-cabin', () => {
      const prof = new THREE.Shape();
      prof.moveTo(-1.98, 1.02);
      prof.lineTo(0.95, 1.02);
      prof.lineTo(0.2, 1.6);
      prof.lineTo(-1.5, 1.62);
      prof.lineTo(-1.98, 1.14);
      prof.closePath();
      const g = new THREE.ExtrudeGeometry(prof, { depth: cabinDepth, bevelEnabled: false });
      g.translate(0, 0, -cabinDepth / 2);
      return g;
    });
    const cabin = new THREE.Mesh(cabinGeo, glass);
    cabin.castShadow = true;
    car.add(cabin);

    // Nóc xe (2 tone đen cho bản cá nhân), trụ A/B/C/D, nẹp chrome, baga mui, vây cá mập, cánh gió
    this.box(car, 1.74, 0.06, cabinDepth + 0.04, isTaxi ? paint : piano, -0.65, 1.64, 0);
    [-1, 1].forEach((sz) => {
      const pz = sz * (cabinDepth / 2 + 0.012);
      this.box(car, 0.95, 0.07, 0.03, piano, 0.575, 1.31, pz, [0, 0, -0.659]);
      this.box(car, 0.68, 0.16, 0.03, isTaxi ? paint : piano, -1.74, 1.38, pz, [0, 0, 0.785]);
      this.box(car, 2.85, 0.025, 0.03, chrome, -0.5, 1.04, pz, null, false);
      this.box(car, 1.5, 0.04, 0.05, chrome, -0.7, 1.69, sz * 0.62);
    });
    this.box(car, 0.1, 0.6, cabinDepth + 0.05, piano, -0.3, 1.32, 0);
    this.box(car, 0.08, 0.6, cabinDepth + 0.05, piano, -1.3, 1.32, 0);
    this.box(car, 0.14, 0.06, 0.05, piano, -1.3, 1.69, 0);
    this.box(car, 0.28, 0.05, cabinDepth, paintDeep, -1.58, 1.62, 0, [0, 0, 0.1]);

    // 3. ĐẦU XE: dải LED cánh chim xuyên suốt + logo V, đèn pha, lưới tản nhiệt dưới
    this.box(car, 0.03, 0.035, W - 0.24, ledWhite, hl + 0.006, 0.97, 0, null, false);
    [-1, 1].forEach((s) => {
      this.box(car, 0.03, 0.15, 0.03, chrome, hl + 0.012, 0.9, s * 0.04, [s * 0.45, 0, 0], false);
      this.box(car, 0.03, 0.09, 0.3, ledWhite, hl + 0.006, 0.8, s * 0.66, null, false);
      this.box(car, 0.03, 0.03, 0.12, amber, hl + 0.007, 0.73, s * 0.72, null, false);
    });
    this.box(car, 0.03, 0.16, 1.0, piano, hl + 0.004, 0.56, 0, null, false);

    // 4. ĐUÔI XE: dải LED hậu đỏ, cụm đèn góc, logo, biển số
    this.box(car, 0.03, 0.05, W - 0.12, ledRed, -hl - 0.006, 1.0, 0, null, false);
    [-1, 1].forEach((s) => {
      this.box(car, 0.03, 0.13, 0.3, ledRed, -hl - 0.006, 0.9, s * 0.72, null, false);
      this.box(car, 0.03, 0.12, 0.03, chrome, -hl - 0.012, 0.86, s * 0.035, [s * 0.45, 0, 0], false);
    });
    const plateTex = this.getPlateTexture(isTaxi ? '30H-886.68' : '30K-168.88', isTaxi ? '#fde047' : '#f8fafc');
    const plateMat = this.texMat(plateTex, {}, true);
    this.plane(car, 0.52, 0.13, plateMat, -hl - 0.008, 0.64, 0, [0, -Math.PI / 2, 0]);
    this.plane(car, 0.52, 0.13, plateMat, hl + 0.03, 0.37, 0, [0, Math.PI / 2, 0]);

    // 5. HÔNG XE: khe cửa, tay nắm, gương chiếu hậu có xi-nhan
    [-1, 1].forEach((sz) => {
      const zf = sz * (hw + 0.006);
      [0.62, -0.3].forEach((xs) => this.box(car, 0.015, 0.66, 0.012, piano, xs, 0.7, zf, null, false));
      [0.3, -0.62].forEach((xs) => this.box(car, 0.16, 0.035, 0.025, chrome, xs, 0.95, sz * (hw + 0.012), null, false));
      this.box(car, 0.08, 0.06, 0.12, piano, 0.86, 1.08, sz * (hw + 0.05));
      this.box(car, 0.18, 0.12, 0.16, paint, 0.84, 1.14, sz * (hw + 0.14));
      this.box(car, 0.02, 0.025, 0.12, amber, 0.935, 1.13, sz * (hw + 0.14), null, false);
    });

    // 6. BÁNH XE mâm 5 chấu lăn được
    car.userData.wheels = [];
    car.userData.wheelRadius = wr;
    [-1, 1].forEach((sx) =>
      [-1, 1].forEach((sz) => {
        car.userData.wheels.push(
          this._addWheel(car, sx * axle, wr, sz * (hw - 0.13), {
            r: wr,
            width: 0.25,
            tire,
            rim: rimDark,
            spoke: chrome,
            spokes: 5,
            outer: sz
          })
        );
      })
    );

    // 7. TAXI: hộp đèn mào trên nóc & decal cửa
    if (isTaxi) {
      const signFace = this.texMat(
        this.getSignTexture({ text: 'TAXI', bg: '#fef08a', fg: '#065f46', aspect: 3.1 }),
        {},
        true
      );
      const signEdge = this.mat('#fef08a', { roughness: 0.4 });
      this.box(car, 0.2, 0.2, 0.62, [signFace, signFace, signEdge, signEdge, signEdge, signEdge], -0.65, 1.77, 0);
      const decal = this.texMat(
        this.getSignTexture({ text: 'TAXI ĐIỆN', bg: paintColor, fg: '#ffffff', aspect: 5 }),
        { transparent: false },
        true
      );
      [-1, 1].forEach((sz) =>
        this.plane(car, 1.0, 0.2, decal, 0.15, 0.6, sz * (hw + 0.007), [0, sz > 0 ? 0 : Math.PI, 0])
      );
    }

    return car;
  }

  // XE SỐ VIỆT NAM (kiểu Wave): gầm hở, yếm, mặt nạ đèn, phuộc, pô inox, tem sọc; options.parked dựng chân chống
  createMotorbike(color = '#dc2626', options = {}) {
    const bike = new THREE.Group();
    bike.name = 'Motorbike_VN';
    const body = new THREE.Group();
    bike.add(body);

    const paint = this.mat(color, { roughness: 0.3, metalness: 0.35 });
    const black = this.mat('#1c1917', { roughness: 0.85 });
    const plastic = this.mat('#3f3f46', { roughness: 0.7 });
    const chrome = this.mat('#e5e7eb', { roughness: 0.15, metalness: 0.95 });
    const metal = this.mat('#9ca3af', { roughness: 0.45, metalness: 0.6 });
    const tire = this.mat('#161616', { roughness: 0.95 });
    const rim = this.mat('#27272a', { roughness: 0.4, metalness: 0.6 });
    const stripe = this.mat(options.stripeColor || '#f8fafc', { roughness: 0.4 });
    const head = this.glow('#fef9c3');
    const amber = this.glow('#f59e0b');
    const red = this.glow('#ef4444');

    const wr = 0.3,
      ax = 0.62;

    // 1. BÁNH XE mâm đúc 5 chấu
    bike.userData.wheels = [ax, -ax].map((x) =>
      this._addWheel(body, x, wr, 0, {
        r: wr,
        width: 0.085,
        tire,
        rim,
        spoke: metal,
        spokes: 5,
        bothSides: true
      })
    );
    bike.userData.wheelRadius = wr;

    // 2. PHUỘC TRƯỚC & DÈ CHẮN BÙN ôm cung bánh
    [-1, 1].forEach((s) => this.box(body, 0.045, 0.66, 0.045, chrome, 0.53, 0.62, s * 0.075, [0, 0, 0.28]));
    this.box(body, 0.3, 0.045, 0.13, paint, ax, 0.665, 0);
    this.box(body, 0.2, 0.045, 0.13, paint, ax + 0.26, 0.562, 0, [0, 0, -0.785]);
    this.box(body, 0.2, 0.045, 0.13, paint, ax - 0.26, 0.562, 0, [0, 0, 0.785]);

    // 3. MẶT NẠ TRƯỚC, YẾM TRONG & ỐP DƯỚI
    this.box(body, 0.2, 0.5, 0.34, paint, 0.46, 0.72, 0, [0, 0, 0.28]);
    this.box(body, 0.06, 0.52, 0.42, plastic, 0.34, 0.7, 0, [0, 0, 0.28]);
    this.box(body, 0.16, 0.18, 0.3, paint, 0.36, 0.44, 0, [0, 0, 0.5]);

    // 4. ĐẦU ĐÈN: ốp tay lái, đèn pha, xi-nhan, tay lái, gương, đồng hồ
    this.box(body, 0.28, 0.14, 0.36, paint, 0.4, 1.02, 0, [0, 0, 0.12]);
    this.box(body, 0.04, 0.08, 0.2, head, 0.545, 1.0, 0, null, false);
    this.box(body, 0.12, 0.03, 0.16, black, 0.3, 1.1, 0);
    [-1, 1].forEach((s) => {
      this.box(body, 0.06, 0.04, 0.05, amber, 0.52, 0.99, s * 0.2, null, false);
      this.box(body, 0.04, 0.035, 0.2, chrome, 0.34, 1.04, s * 0.26);
      this.box(body, 0.055, 0.055, 0.12, black, 0.34, 1.04, s * 0.39);
      this.box(body, 0.015, 0.2, 0.015, chrome, 0.34, 1.16, s * 0.24, [s * 0.25, 0, 0], false);
      this.box(body, 0.03, 0.07, 0.11, black, 0.34, 1.26, s * 0.27);
    });

    // 5. KHUNG GẦM HỞ (underbone), ỐP THÂN GIỮA, ĐỘNG CƠ, GÁC CHÂN
    this.box(body, 0.5, 0.08, 0.08, black, 0.26, 0.64, 0, [0, 0, 0.84]);
    this.box(body, 0.34, 0.12, 0.2, paint, 0.2, 0.52, 0, [0, 0, 0.55]);
    this.box(body, 0.36, 0.26, 0.24, metal, 0.0, 0.33, 0);
    this.box(
      body,
      0.2,
      0.16,
      0.18,
      this.mat('#6b7280', { metalness: 0.5, roughness: 0.5 }),
      0.2,
      0.38,
      0,
      [0, 0, -1.1]
    );
    this.cyl(body, 0.1, 0.1, 0.03, 14, chrome, -0.02, 0.3, 0.135, [Math.PI / 2, 0, 0]);
    [-1, 1].forEach((s) => {
      this.box(body, 0.05, 0.03, 0.14, black, 0.08, 0.25, s * 0.18);
      this.box(body, 0.04, 0.03, 0.1, black, -0.42, 0.42, s * 0.19);
    });

    // 6. GẮP SAU, HỘP XÍCH TRÁI, PHUỘC SAU ĐÔI
    this.box(body, 0.64, 0.05, 0.04, black, -0.31, 0.31, 0.09);
    this.box(body, 0.64, 0.11, 0.035, black, -0.31, 0.32, -0.1);
    [-1, 1].forEach((s) =>
      this.box(
        body,
        0.035,
        0.36,
        0.035,
        this.mat('#27272a', { roughness: 0.5, metalness: 0.4 }),
        -0.55,
        0.5,
        s * 0.13,
        [0, 0, -0.29]
      )
    );

    // 7. THÂN SAU, TEM SỌC, YÊN 2 LỚP, TAY DẮT
    this.box(body, 0.74, 0.2, 0.26, paint, -0.44, 0.66, 0, [0, 0, 0.06]);
    this.box(body, 0.22, 0.14, 0.2, paint, -0.86, 0.73, 0, [0, 0, 0.12]);
    [-1, 1].forEach((s) => this.box(body, 0.6, 0.035, 0.01, stripe, -0.44, 0.64, s * 0.132, [0, 0, 0.06], false));
    this.box(body, 0.78, 0.08, 0.27, black, -0.4, 0.8, 0);
    this.box(body, 0.66, 0.05, 0.22, black, -0.38, 0.86, 0);
    this.box(body, 0.22, 0.03, 0.24, chrome, -0.78, 0.84, 0);
    [-1, 1].forEach((s) => this.box(body, 0.3, 0.03, 0.03, chrome, -0.66, 0.83, s * 0.12));

    // 8. ĐÈN HẬU, XI-NHAN SAU, CHẮN BÙN SAU & BIỂN SỐ 2 DÒNG
    this.box(body, 0.05, 0.07, 0.14, red, -0.975, 0.76, 0, null, false);
    [-1, 1].forEach((s) => this.box(body, 0.04, 0.04, 0.04, amber, -0.96, 0.72, s * 0.1, null, false));
    this.box(body, 0.3, 0.04, 0.12, black, -0.74, 0.58, 0, [0, 0, -0.5]);
    this.box(body, 0.03, 0.2, 0.1, black, -0.94, 0.58, 0);
    const plate = this.texMat(this.getPlateTexture(options.plate || '29-X1\n999.99'), {}, true);
    this.plane(body, 0.17, 0.13, plate, -0.958, 0.52, 0, [0, -Math.PI / 2, 0]);

    // 9. PÔ INOX BÊN PHẢI (+Z) & ỐP CHỐNG BỎNG
    this.box(body, 0.42, 0.045, 0.045, chrome, 0.02, 0.18, 0.1, [0, 0, 0.1]);
    this.cyl(body, 0.055, 0.06, 0.52, 12, chrome, -0.46, 0.3, 0.17, [0, 0, Math.PI / 2 - 0.22]);
    this.box(body, 0.34, 0.07, 0.02, black, -0.44, 0.32, 0.23, [0, 0, 0.22]);

    // 10. ĐỖ XE: chân chống nghiêng bên trái & xe nghiêng nhẹ
    if (options.parked) {
      this.box(body, 0.03, 0.32, 0.03, black, -0.05, 0.15, -0.2, [-0.55, 0, 0.3]);
      body.rotation.x = -0.07;
    }

    return bike;
  }
}

export function registerVehicleMethods(proto) {
  Object.getOwnPropertyNames(MethodsHolder.prototype).forEach((name) => {
    if (name !== 'constructor') {
      proto[name] = MethodsHolder.prototype[name];
    }
  });
}
